import { spreads } from '../data/spreads';
import { tEn, tZh } from '../i18n';
import type { DisposableScreen, Spread } from '../types';
import { escapeHtml } from './escape';

export function mountFormations(
  result: { question: string; isGeneral: boolean },
  onPick: (spread: Spread) => void,
): HTMLElement {
  const screen = document.createElement('section');
  screen.id = 'screen-formations';
  screen.className = 'screen active';

  const qHtml = result.isGeneral
    ? `<div class="user-q general"><span lang="en"><em>${tEn('formations.general')}</em></span><span lang="zh">${tZh('formations.general')}</span></div>`
    : `<div class="user-q">&ldquo;${escapeHtml(result.question)}&rdquo;</div>`;

  screen.innerHTML = `
    <div class="formations-title">
      <div class="main"><span lang="en"><em>${tEn('formations.title')}</em></span><span lang="zh">${tZh('formations.title')}</span></div>
      ${qHtml}
    </div>
    <div class="formations-grid">
      ${spreads.map((s, i) => formationCardHtml(s, i + 1)).join('')}
    </div>
    <div class="tooltip" id="formation-tooltip"></div>
  `;

  const tooltip = screen.querySelector('#formation-tooltip') as HTMLElement;
  const hideTooltip = (): void => tooltip.classList.remove('show');

  // Single-open accordion: only one formation card's inline description
  // is open at a time. The class lives on the .formation root; CSS in
  // formations.css drives the max-height transition.
  const closeAllDesc = (): void => {
    screen.querySelectorAll('.formation.show-desc').forEach((other) => {
      other.classList.remove('show-desc');
    });
  };

  // Screen-level dismiss handler:
  // - clears the floating cursor-following hover tooltip (desktop)
  // - closes the open inline mobile expansion *unless* the tap landed
  //   inside the open card (so its name/body/expansion taps stay scoped
  //   to the card's own click handler).
  // AbortController scopes the window resize listener so navigating
  // home and back doesn't accumulate stale listeners.
  const listeners = new AbortController();
  const opts = { signal: listeners.signal };
  screen.addEventListener(
    'click',
    (e) => {
      hideTooltip();
      if (!(e.target as HTMLElement).closest('.formation.show-desc')) {
        closeAllDesc();
      }
    },
    opts,
  );
  window.addEventListener(
    'resize',
    () => {
      hideTooltip();
      closeAllDesc();
    },
    opts,
  );
  (screen as DisposableScreen).dispose = () => listeners.abort();

  screen.querySelectorAll<HTMLElement>('.formation').forEach((el) => {
    const spread = spreads.find((s) => s.id === el.dataset.spread) as Spread;
    el.addEventListener('mouseenter', () => {
      renderTooltip(tooltip, spread);
    });
    el.addEventListener('mousemove', (e) => {
      tooltip.style.left = `${e.clientX}px`;
      tooltip.style.top = `${e.clientY - 20}px`;
    });
    el.addEventListener('mouseleave', hideTooltip);
    el.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const isTouch = window.matchMedia('(hover: none)').matches;
      // Mobile: tap on the spread name toggles the inline description.
      // stopPropagation prevents the screen-level handler from immediately
      // closing what we just opened.
      if (isTouch && target.closest('.f-name')) {
        e.stopPropagation();
        const wasOpen = el.classList.contains('show-desc');
        closeAllDesc();
        if (!wasOpen) el.classList.add('show-desc');
        return;
      }
      // Mobile: tap on the inline description itself is a no-op. Without
      // stopPropagation it would bubble to the .formation listener and
      // mis-pick the spread.
      if (isTouch && target.closest('.f-desc')) {
        e.stopPropagation();
        return;
      }
      // Desktop click anywhere on the card, or mobile tap on the body
      // (preview/label/padding), picks the spread.
      onPick(spread);
    });
  });

  return screen;
}

function formationCardHtml(s: Spread, idx: number): string {
  const layoutClass = layoutCssClass(s.layout);
  const minis = Array.from({ length: s.count }, () => '<div class="mini"></div>').join('');
  // Inline .f-desc is hidden on desktop (CSS) and animated open on
  // mobile when .formation gains .show-desc. Bilingual <span lang>
  // pattern so a mid-expansion language switch updates copy live.
  return `
    <div class="formation" data-spread="${s.id}">
      <div class="f-preview"><div class="${layoutClass}">${minis}</div></div>
      <div class="f-label">${String(idx).padStart(2, '0')}</div>
      <div class="f-name">
        <span lang="en"><em>${s.name_en}</em></span>
        <span lang="zh">${s.name_zh}</span>
      </div>
      <div class="f-desc">
        <span lang="en">${escapeHtml(s.good_for_en)}</span><span lang="zh">${escapeHtml(s.good_for_zh)}</span>
      </div>
    </div>`;
}

function layoutCssClass(layout: Spread['layout']): string {
  const map: Record<Spread['layout'], string> = {
    single: 'l-single',
    horizontal: 'l-triple',
    arch: 'l-arch',
    diagonal: 'l-diagonal',
    row: 'l-row',
    diamond: 'l-diamond',
    two_three: 'l-23',
  };
  return map[layout];
}

function renderTooltip(tooltip: HTMLElement, s: Spread): void {
  // Bilingual <span lang> pattern so a mid-tooltip language switch is
  // CSS-reactive without re-running the render path.
  tooltip.innerHTML = `
    <div class="t-name"><span lang="en">${escapeHtml(s.name_en)}</span><span lang="zh">${escapeHtml(s.name_zh)}</span></div>
    <div class="t-good"><span lang="en">${escapeHtml(s.good_for_en)}</span><span lang="zh">${escapeHtml(s.good_for_zh)}</span></div>`;
  tooltip.classList.add('show');
}
